import { useCallback, useEffect, useState } from "react";
import { Loader2, Mail, Plus, Send, Trash2 } from "lucide-react";
import AdminShell from "@/components/admin/AdminShell";
import { adminApi } from "@/lib/adminApi";

/** Newsletter Center — drafts, history, subscriber count and sending via the
 * existing Resend integration. Sending only happens on explicit founder
 * action; there is no scheduler. Open/click rates need Resend Broadcasts. */

export default function AdminNewsletter() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null); // {id?, subject, body_markdown, scheduled_for}
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    try { setData(await adminApi.newsletterList()); }
    catch (e) { setErr(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true); setErr(""); setMsg("");
    try {
      if (editing.id) await adminApi.newsletterUpdate(editing.id, editing);
      else await adminApi.newsletterCreate(editing);
      setEditing(null); setMsg("Draft saved.");
      await load();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this draft?")) return;
    try { await adminApi.newsletterDelete(id); await load(); }
    catch (e) { setErr(e.message); }
  };

  const send = async (nl) => {
    setErr(""); setMsg("");
    try {
      const dry = await adminApi.newsletterSend(nl.id, true);
      if (!window.confirm(`Send "${nl.subject}" to ${dry.recipients} subscriber(s) now? This cannot be undone.`)) return;
      const res = await adminApi.newsletterSend(nl.id, false);
      setMsg(`Sending to ${res.recipients} subscriber(s) in the background…`);
      setTimeout(load, 4000);
    } catch (e) { setErr(e.message); }
  };

  const stats = data?.stats || {};
  const list = data?.newsletters || [];
  const drafts = list.filter((n) => n.status !== "sent");
  const sent = list.filter((n) => n.status === "sent");

  return (
    <AdminShell title="Newsletter Center">
      <p className="text-sm text-bone/60 -mt-6 mb-6 max-w-3xl">
        Drafts, history and sending via Resend to every subscriber. Sending is always an explicit
        action with confirmation — nothing goes out automatically. A scheduled date is a label only.
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8" data-testid="newsletter-stats">
        {[
          ["Subscribers", stats.subscribers],
          ["Newsletters sent", stats.sent_total],
          ["Open rate", "n/a"],
          ["Click rate", "n/a"],
        ].map(([l, v]) => (
          <div key={l} className="px-4 py-3 border border-white/10 bg-white/[0.02]">
            <p className="text-2xl font-serif text-bone leading-none">{v ?? "—"}</p>
            <p className="mt-1.5 text-[10px] tracking-[0.18em] uppercase text-bone/50">{l}</p>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-bone/40 -mt-5 mb-8" data-testid="newsletter-rates-note">{stats.rates_note}</p>

      {!editing && (
        <button onClick={() => setEditing({ subject: "", body_markdown: "", scheduled_for: "" })}
          data-testid="newsletter-new-draft"
          className="inline-flex items-center gap-2 mb-8 px-5 py-2.5 text-[11px] tracking-[0.24em] uppercase bg-gold text-ink hover:bg-gold-hover">
          <Plus size={13} strokeWidth={1.75} /> New draft
        </button>
      )}

      {editing && (
        <div className="border border-gold/25 bg-white/[0.02] p-5 mb-10" data-testid="newsletter-editor">
          <p className="text-[10px] tracking-[0.28em] uppercase text-gold/70 mb-4">{editing.id ? "Edit draft" : "New draft"}</p>
          <input value={editing.subject} onChange={(e) => setEditing({ ...editing, subject: e.target.value })}
            placeholder="Subject line…" data-testid="newsletter-subject"
            className="w-full mb-3 bg-white/[0.03] border border-white/15 focus:border-gold/60 outline-none px-3 py-2.5 text-sm text-bone" />
          <textarea value={editing.body_markdown} onChange={(e) => setEditing({ ...editing, body_markdown: e.target.value })}
            placeholder="Body (Markdown)…" rows={12} data-testid="newsletter-body"
            className="w-full mb-3 bg-white/[0.03] border border-white/15 focus:border-gold/60 outline-none px-3 py-2.5 text-sm text-bone font-mono" />
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-[11px] text-bone/50 uppercase tracking-wide">Planned date (label only)</label>
            <input type="date" value={editing.scheduled_for || ""} onChange={(e) => setEditing({ ...editing, scheduled_for: e.target.value })}
              data-testid="newsletter-scheduled-for"
              className="bg-white/[0.03] border border-white/15 text-bone text-[12px] px-3 py-2 outline-none" />
            <button onClick={save} disabled={busy || !editing.subject.trim()} data-testid="newsletter-save"
              className="ml-auto px-5 py-2.5 text-[11px] tracking-[0.24em] uppercase bg-gold text-ink hover:bg-gold-hover disabled:opacity-40">
              {busy ? "Saving…" : "Save draft"}
            </button>
            <button onClick={() => setEditing(null)} data-testid="newsletter-cancel"
              className="px-4 py-2.5 text-[11px] tracking-[0.24em] uppercase border border-white/15 text-bone/60 hover:border-gold/40">Cancel</button>
          </div>
        </div>
      )}

      <h2 className="text-[11px] tracking-[0.28em] uppercase text-gold/70 mb-3">Drafts & planned</h2>
      <div className="border border-white/10 bg-white/[0.02] mb-10 divide-y divide-white/5" data-testid="newsletter-drafts">
        {drafts.length === 0 && <p className="px-4 py-4 text-sm text-bone/40">No drafts.</p>}
        {drafts.map((n) => (
          <div key={n.id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`newsletter-draft-${n.id}`}>
            <Mail size={13} className="text-bone/40 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-bone truncate">{n.subject || "(no subject)"}</p>
              <p className="text-[10px] text-bone/40">
                {n.status === "sending" ? "SENDING…" : "draft"}
                {n.scheduled_for ? ` · planned ${n.scheduled_for}` : ""} · updated {String(n.updated_at).slice(0, 10)}
              </p>
            </div>
            <button onClick={() => setEditing({ id: n.id, subject: n.subject, body_markdown: n.body_markdown, scheduled_for: n.scheduled_for || "" })}
              data-testid={`newsletter-edit-${n.id}`}
              className="text-[10px] tracking-[0.2em] uppercase border border-white/15 px-3 py-1.5 text-bone/60 hover:border-gold/40 hover:text-gold">Edit</button>
            <button onClick={() => send(n)} disabled={n.status === "sending"} data-testid={`newsletter-send-${n.id}`}
              className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase border border-gold/40 px-3 py-1.5 text-gold hover:bg-gold/10 disabled:opacity-40">
              {n.status === "sending" ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />} Send
            </button>
            <button onClick={() => remove(n.id)} data-testid={`newsletter-delete-${n.id}`}
              className="text-bone/40 hover:text-red-400"><Trash2 size={13} /></button>
          </div>
        ))}
      </div>

      <h2 className="text-[11px] tracking-[0.28em] uppercase text-gold/70 mb-3">Previously sent</h2>
      <div className="border border-white/10 bg-white/[0.02] divide-y divide-white/5" data-testid="newsletter-sent">
        {sent.length === 0 && <p className="px-4 py-4 text-sm text-bone/40">Nothing sent yet.</p>}
        {sent.map((n) => (
          <div key={n.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
            <Send size={13} className="text-emerald-300 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-bone truncate">{n.subject}</p>
              <p className="text-[10px] text-bone/40">sent {String(n.sent_at).slice(0, 16).replace("T", " ")} · {n.recipients_count} recipient(s){n.failed_count ? ` · ${n.failed_count} failed` : ""}</p>
            </div>
            <span className="text-[10px] tracking-[0.2em] uppercase text-bone/40">open/click: n/a</span>
          </div>
        ))}
      </div>

      {msg && <p className="mt-4 text-sm text-emerald-300" data-testid="newsletter-msg">{msg}</p>}
      {err && <p className="mt-4 text-sm text-red-400" data-testid="newsletter-error">{err}</p>}
    </AdminShell>
  );
}
